import React, {
  Dispatch,
  KeyboardEvent,
  MouseEvent,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { uriTransformer } from 'react-markdown'

import {
  DrawerContentContainer,
  StyledDrawer,
  StyledAgreementModal,
  DrawerTitle,
  DrawerBody,
  DrawerImage,
} from './styled-components'

import { TestIDs } from 'test/utils/testIds'
import AgreementDoc from 'components/AgreementDoc'
import { LinkRenderer } from '../shared-components'
import {
  trackRegistrationContextualFunnelBannerDrawerDismissedPayload,
  trackRegistrationContextualFunnelBannerDrawerTermsTappedPayload,
  trackRegistrationContextualFunnelBannerDrawerTermsViewedPayload,
  trackRegistrationContextualFunnelBannerDrawerViewedPayload,
} from 'generated/trackingEvents'
import { translateEventProps } from 'utils/analytics'
import { useAnalyticsClient } from '@acorns/web-utils'

const ANCHOR_TAG = 'a'

interface Props {
  isOpen: boolean
  setIsOpen: Dispatch<SetStateAction<boolean>>
  body: string
  image: string
  title: string
  eventData: any
}

export const Drawer = (props: Props) => {
  const [isDrawerLinkModalOpen, setIsDrawerLinkModalOpen] = useState(false)
  const [drawerLink, setDrawerlink] = useState('')
  const analytics = useAnalyticsClient()
  const [isAgreementLoaded, setAgreementLoaded] = useState(false)

  const handleDrawerLink = (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).tagName.toLowerCase() === ANCHOR_TAG) {
      const [
        tappedEventName,
        tappedPayload,
      ] = trackRegistrationContextualFunnelBannerDrawerTermsTappedPayload(props.eventData)
      analytics.track(tappedEventName, translateEventProps(tappedPayload))
      setIsDrawerLinkModalOpen(true)
    }
  }

  const handleCloseDrawer = () => {
    const [eventName, payload] = trackRegistrationContextualFunnelBannerDrawerDismissedPayload(
      props.eventData,
    )
    analytics.track(eventName, translateEventProps(payload))
    props.setIsOpen(false)
  }

  const handleCloseDrawerLinkModal = () => {
    setAgreementLoaded(false)
    setIsDrawerLinkModalOpen(false)
  }

  useEffect(() => {
    if (!isAgreementLoaded) return
    const [eventName, payload] = trackRegistrationContextualFunnelBannerDrawerTermsViewedPayload(
      props.eventData,
    )
    analytics.track(eventName, translateEventProps(payload))
  }, [isAgreementLoaded, analytics, props.eventData])

  useEffect(() => {
    if (!props.isOpen) return
    const [eventName, payload] = trackRegistrationContextualFunnelBannerDrawerViewedPayload(
      props.eventData,
    )
    analytics.track(eventName, translateEventProps(payload))
  }, [analytics, props.eventData, props.isOpen])

  /**
   * This is a somewhat hacky way to get the library to give us
   * the uri from the markdown content, not a long term solution,
   * we should look into another markdown library or updating the current
   * react-markdown library to address this.
   */
  const handleUriTransform = (uri: string) => {
    setDrawerlink(uri)
    return uriTransformer(uri)
  }

  return (
    <>
      <StyledDrawer isOpen={props.isOpen} onClose={handleCloseDrawer}>
        <DrawerContentContainer>
          <DrawerTitle data-testid={TestIDs.DRAWER.TITLE}>{props.title}</DrawerTitle>
          <div
            data-testid={TestIDs.DRAWER.BODY}
            onClick={handleDrawerLink}
            onKeyDown={handleDrawerLink}
          >
            <DrawerBody
              source={props.body}
              linkTarget="_blank"
              renderers={{ Link: LinkRenderer }}
              transformLinkUri={handleUriTransform}
            />
          </div>
        </DrawerContentContainer>
        <DrawerImage data-testid={TestIDs.DRAWER.IMAGE} src={props.image} alt="Drawer Image" />
      </StyledDrawer>
      {drawerLink && (
        <StyledAgreementModal
          isOpen={isDrawerLinkModalOpen}
          onCloseClick={handleCloseDrawerLinkModal}
        >
          <AgreementDoc src={drawerLink} setAgreementLoaded={setAgreementLoaded} />
        </StyledAgreementModal>
      )}
    </>
  )
}
